import { useMemo } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { fmtDateLong, fmtHour, fmtTime, isoDow, monthLabel, monthMatrix, slotsForDate, torontoTodayKey } from "@/lib/booking";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MAX_OFFSET = 2;

export default function CalendarView({ monthOffset, setMonthOffset, date, onDate, slot, onSlot, av }) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + monthOffset;
  const cells = useMemo(() => monthMatrix(year, month), [year, month]);
  const todayKey = torontoTodayKey();
  const slots = useMemo(() => (date ? slotsForDate(date, av) : []), [date, av]);
  const daysLabel = av.weekdays.length
    ? av.weekdays
        .slice()
        .sort((a, b) => a - b)
        .map((d) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d])
        .join(", ")
    : "no days";
  const hoursLabel = `${fmtHour(av.startHour)} – ${fmtHour(av.endHour)}`;

  return (
    <div data-testid="booking-calendar">
      <div className="flex items-center justify-between">
        <p className="font-serif text-xl text-ink" data-testid="calendar-month-label">
          {monthLabel(year, month)}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="calendar-prev-month"
            onClick={() => setMonthOffset((m) => Math.max(0, m - 1))}
            disabled={monthOffset === 0}
            className="flex h-9 w-9 items-center justify-center border border-hairline text-ink transition-colors hover:bg-ink hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink"
            aria-label="Previous month"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            type="button"
            data-testid="calendar-next-month"
            onClick={() => setMonthOffset((m) => Math.min(MAX_OFFSET, m + 1))}
            disabled={monthOffset === MAX_OFFSET}
            className="flex h-9 w-9 items-center justify-center border border-hairline text-ink transition-colors hover:bg-ink hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink"
            aria-label="Next month"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-7 gap-y-1 text-center">
        {WEEKDAYS.map((d, i) => (
          <span key={`${d}-${i}`} className="pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-smoke">
            {d}
          </span>
        ))}
        {cells.map((cell, i) =>
          cell === null ? (
            <span key={`empty-${i}`} />
          ) : (
            (() => {
              const off = !av.weekdays.includes(isoDow(cell.dow));
              return (
                <button
                  key={cell.key}
                  type="button"
                  data-testid="calendar-date-cell"
                  data-date={cell.key}
                  disabled={off || cell.key <= todayKey}
                  onClick={() => {
                    onDate(cell.key);
                    onSlot(null);
                  }}
                  className={[
                    "mx-auto flex h-9 w-9 items-center justify-center text-[13px] transition-colors",
                    cell.key === date
                      ? "bg-ink font-semibold text-white"
                      : off || cell.key <= todayKey
                        ? "cursor-not-allowed text-ink/25"
                        : "text-ink hover:bg-greige",
                  ].join(" ")}
                >
                  {cell.day}
                </button>
              );
            })()
          )
        )}
      </div>

      {date && (
        <div className="mt-6 border-t border-hairline pt-5">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-smoke">
            <Clock size={12} />
            {fmtDateLong(slots.length ? slots[0].utc : new Date())} · 30-min slots
          </p>
          {slots.length === 0 ? (
            <p className="mt-3 text-sm text-smoke" data-testid="no-slots-message">
              No times remaining on this day — please choose another date.
            </p>
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {slots.map((s) => (
                <button
                  key={s.time}
                  type="button"
                  data-testid="time-slot-button"
                  data-time={s.time}
                  onClick={() => onSlot({ ...s, dateKey: date })}
                  className={[
                    "border px-2 py-2.5 text-[12px] font-medium transition-colors",
                    slot && slot.time === s.time && slot.dateKey === date
                      ? "border-ink bg-ink text-white"
                      : "border-hairline text-ink hover:border-ink",
                  ].join(" ")}
                >
                  {fmtTime(s.utc)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!date && (
        <p className="mt-6 border-t border-hairline pt-5 text-sm font-light text-smoke" data-testid="calendar-empty-hint">
          Choose a day to see available times. Consultations run {daysLabel}, {hoursLabel} (Toronto).
        </p>
      )}
    </div>
  );
}